import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { imgOpt } from '@fshr/shared';
import { CTA } from '@micro-apps/shared-react';
import { getConfig } from '@fshr/shared-react/config';
import { useHotelDiningCarousel } from './use-hotel-dining-carousel';

const breakpoints = { xl: 1701, lg: 961, md: 701 };

interface AlternateImage {
	cropping: string | null;
	path: string;
}

interface BackgroundImage {
	nanoid: string;
	alternateImages: { large: AlternateImage; medium: AlternateImage; small: AlternateImage };
	imageSizes: { xl2x: number; xl: number; lg2x: number; lg: number; md2x: number; md: number };
}

interface ButtonData {
	text?: string;
	link?: string;
	label?: string;
	modalId?: string;
	warningModal?: string | boolean;
}

interface WarningModalData {
	id: string;
	hasImage?: boolean;
	nestedList?: boolean;
	children?: { widget?: WidgetData };
}

interface WidgetData {
	id: string;
	modalId: string;
	name: string;
	enName: string;
	address: string;
	image: string;
	occasions?: string[];
	alternateAvailabilityIds?: string[];
	orsCode: string;
	channel: string;
	enRegion: string;
	enCountry: string;
	stripePublicKey: string;
	launcherMode?: boolean;
	stores?: string[];
	translations?: Record<string, string>;
	clientEnv?: string;
}

interface DiningCard {
	id: string;
	eyebrow: string;
	title: string;
	description: string;
	backgroundImage: BackgroundImage | null;
	primaryButton?: ButtonData;
	secondaryButton?: ButtonData;
	textCtaOne?: ButtonData;
	textCtaTwo?: ButtonData;
	warningModal?: WarningModalData;
	indicator?: string;
	indicatorStatus?: string;
}

interface CarouselData {
	id?: string;
	heading?: { title?: string };
	cards?: Record<string, unknown>;
}

function getPropertyContext(): { lang: string; propertyShortName: string } | null {
	const info = (window as any).FS?.propertyInfo;
	if (!info) return null;
	const lang: string = info.language ?? 'en';
	const path: string = info.path ?? window.location.pathname;
	const parts = path.split('/').filter(Boolean);
	const idx = parts.indexOf('properties');
	const propertyShortName = idx >= 0 ? parts[idx + 1] : undefined;
	if (!propertyShortName) return null;
	return { lang, propertyShortName };
}

function extractCards(raw: Record<string, unknown>): DiningCard[] {
	return Object.entries(raw).map(([key, value]) => {
		const card = (value as any)?.genericCard ?? (value as any);
		return {
			id: key,
			eyebrow: card?.eyebrow ?? '',
			title: card?.title ?? '',
			description: card?.text?.text ?? '',
			backgroundImage: card?.background_image ?? null,
			primaryButton: card?.primaryButton,
			secondaryButton: card?.secondaryButton,
			textCtaOne: card?.textCtaOne,
			textCtaTwo: card?.textCtaTwo,
			warningModal: card?.warning_modal,
			indicator: card?.indicator,
			indicatorStatus: card?.indicator_status,
		};
	});
}

function BackgroundImageCard({ backgroundImage }: { backgroundImage: BackgroundImage }) {
	const { nanoid, alternateImages, imageSizes } = backgroundImage;
	if (!alternateImages || !imageSizes) return null;
	const { large, medium, small } = alternateImages;
	if (!large || !medium || !small) return null;
	return (
		<div className={`BackgroundImage BackgroundImage-${nanoid} AnimatedCardCarousel-Card-image`}>
			<picture className="BackgroundImage-picture">
				<source
					srcSet={imgOpt({
						cropping: large.cropping ?? '',
						path: large.path,
						size: String(imageSizes.xl2x),
						quality: 65,
						mode: 'author',
					})}
					media={`only screen and (-webkit-min-device-pixel-ratio: 1.5) and (min-width: ${breakpoints.lg}px), only screen and (min-resolution: 1.5dppx) and (min-width: ${breakpoints.lg}px)`}
				/>
				<source
					srcSet={imgOpt({
						cropping: large.cropping ?? '',
						path: large.path,
						size: String(imageSizes.xl),
						quality: 65,
						mode: 'author',
					})}
					media={`only screen and (min-width: ${breakpoints.lg}px)`}
				/>
				<source
					srcSet={imgOpt({
						cropping: medium.cropping ?? '',
						path: medium.path,
						size: String(imageSizes.lg2x),
						quality: 70,
						mode: 'author',
					})}
					media={`only screen and (-webkit-min-device-pixel-ratio: 1.5) and (min-width: ${breakpoints.md}px), only screen and (min-resolution: 1.5dppx) and (min-width: ${breakpoints.md}px)`}
				/>
				<source
					srcSet={imgOpt({
						cropping: medium.cropping ?? '',
						path: medium.path,
						size: String(imageSizes.lg),
						quality: 70,
						mode: 'author',
					})}
					media={`only screen and (min-width: ${breakpoints.md}px)`}
				/>
				<source
					srcSet={imgOpt({
						cropping: small.cropping ?? '',
						path: small.path,
						size: String(imageSizes.md2x),
						quality: 75,
						mode: 'author',
					})}
					media="only screen and (-webkit-min-device-pixel-ratio: 1.5), only screen and (min-resolution: 1.5dppx)"
				/>
				<img
					loading="lazy"
					src={imgOpt({
						cropping: small.cropping ?? '',
						path: small.path,
						size: String(imageSizes.md),
						quality: 75,
						mode: 'author',
					})}
					alt=""
					className="BackgroundImage-img"
				/>
			</picture>
		</div>
	);
}

function CardBody({
	card,
	onOpenModal,
}: {
	card: DiningCard;
	onOpenModal?: (modalId: string) => void;
}) {
	const { primaryButton, secondaryButton, textCtaOne, textCtaTwo } = card;
	const hasBothButtons = !!(secondaryButton?.link && secondaryButton?.text);

	const isModalTrigger = !!(primaryButton?.warningModal && primaryButton?.modalId && onOpenModal);

	const handlePrimaryClick = isModalTrigger
		? (e: React.MouseEvent) => {
				e.preventDefault();
				onOpenModal!(primaryButton!.modalId!);
			}
		: undefined;

	// Modal triggers render as <a href=""> to match the Dining page's native carousel pattern,
	// enabling the shared [href=''] CSS selector for Optimizely click_events tracking on both pages.
	const primaryBaseClass = `AnimatedCardCarousel-Card-CTA AnimatedCardCarousel-Card-CTA--button${hasBothButtons ? '' : ' AnimatedCardCarousel-Card-CTA--button--primary'}`;

	return (
		<div className="AnimatedCardCarousel-Card-body">
			<div className="AnimatedCardCarousel-Card-heading">
				{card.eyebrow && (
					<p className="AnimatedCardCarousel-Card-heading--eyebrow ty-h4">{card.eyebrow}</p>
				)}
				<h3 className="AnimatedCardCarousel-Card-heading--title AnimatedCardCarousel-Card-heading--title--hasBorder ty-c1">
					{card.title}
				</h3>
			</div>
			{card.description && (
				<div className="normal AnimatedCardCarousel-Card-description ty-b3">{card.description}</div>
			)}
			<div className="AnimatedCardCarousel-Card-CTAContainer">
				{primaryButton?.text && (
					<>
						{isModalTrigger ? (
							<a
								href=""
								className={`${primaryBaseClass} CTA CTA--primary--light`}
								aria-label={primaryButton.label}
								onClick={handlePrimaryClick}
							>
								{primaryButton.text}
							</a>
						) : (
							<CTA
								type="primary"
								theme="light"
								link={primaryButton.link}
								label={primaryButton.label}
								className={primaryBaseClass}
							>
								{primaryButton.text}
							</CTA>
						)}
						{hasBothButtons && (
							<CTA
								type="secondary"
								theme="light"
								link={secondaryButton!.link}
								label={secondaryButton!.label}
								className="AnimatedCardCarousel-Card-CTA AnimatedCardCarousel-Card-CTA--button AnimatedCardCarousel-Card-CTA--button--secondary"
							>
								{secondaryButton!.text}
							</CTA>
						)}
					</>
				)}
				{[textCtaOne, textCtaTwo].map((cta, i) =>
					cta?.link ? (
						<CTA
							key={i}
							type="underlined"
							theme="light"
							link={cta.link}
							label={cta.label}
							className="ty-c4 AnimatedCardCarousel-Card-CTA AnimatedCardCarousel-Card-CTA--link"
						>
							{cta.text}
						</CTA>
					) : null
				)}
			</div>
		</div>
	);
}

function getScriptContent(clientEnv: string): string | null {
	const match = clientEnv.match(/<script>([\s\S]*?)<\/script>/);
	return match ? match[1] : null;
}

function WarningModalComponent({
	warningModal,
	isOpen,
	onClose,
}: {
	warningModal: WarningModalData;
	isOpen: boolean;
	onClose: () => void;
}) {
	const lazyRenderRef = useRef<HTMLDivElement>(null);
	const hasBeenOpenedRef = useRef(false);
	const { id, hasImage, nestedList, children } = warningModal;
	const widget = children?.widget;

	useEffect(() => {
		const el = lazyRenderRef.current;
		if (!el) return;
		el.dispatchEvent(new CustomEvent('react.register', { bubbles: true }));
	}, []);

	useEffect(() => {
		if (isOpen) hasBeenOpenedRef.current = true;
		const el = lazyRenderRef.current;
		if (!el) return;
		const $ = (window as any).$;
		if (!$) return;
		if (isOpen) {
			$(el).trigger('react.mount');
		} else if (hasBeenOpenedRef.current) {
			// Only unmount after the modal has been opened at least once.
			// Triggering react.unmount on initial mount (isOpen=false) bubbles up
			// via jQuery's pre-built propagation path and destroys ancestor roots.
			$(el).trigger('react.unmount');
		}
	}, [isOpen]);

	return (
		<div
			className={`WarningModal${isOpen ? ' WarningModal--opened' : ''}`}
			id={id}
			style={{ display: isOpen ? 'block' : 'none' }}
		>
			<div className="WarningModal-background" role="presentation" onClick={onClose}></div>
			<div
				className={`WarningModal-content${hasImage ? ' WarningModal-content-withImage' : ''}${nestedList ? ' WarningModal-content-nestedList' : ''}`}
			>
				{widget && (
					<>
						{widget.clientEnv && (
							<script
								dangerouslySetInnerHTML={{
									__html: getScriptContent(widget.clientEnv) ?? '',
								}}
							/>
						)}
						<div
							ref={lazyRenderRef}
							className="react-data OpenTable OpenTable-react-ssr-placeholder lazy-render"
							data-component-name="OpenTable"
							data-id={widget.id}
							data-modal-id={widget.modalId}
							data-name={widget.name}
							data-en-name={widget.enName}
							data-address={widget.address}
							data-image={widget.image}
							data-occasions={JSON.stringify(widget.occasions ?? [])}
							data-alternate-availability-ids={JSON.stringify(
								widget.alternateAvailabilityIds ?? []
							)}
							data-disable-alternate-availability="false"
							data-property-ors-code={widget.orsCode}
							data-property-name={widget.channel}
							data-property-region={widget.enRegion}
							data-property-subregion={widget.enCountry}
							data-stripe-public-key={widget.stripePublicKey}
							data-analytics-enabled="true"
							data-launcher-mode={widget.launcherMode ? 'true' : 'false'}
							data-disable-prepopulating="false"
							data-stores={JSON.stringify(widget.stores ?? [])}
							data-translations-string={JSON.stringify(widget.translations ?? {})}
						></div>
					</>
				)}
				<button className="Button--close--light WarningModal-closeCTA" onClick={onClose}>
					<span className="Button-label">Close overlay</span>
				</button>
			</div>
		</div>
	);
}

// Memoized so that activeModal state changes in the parent don't re-render the Flickity-managed
// DOM. Flickity adds inline styles and classes (is-selected, animated, height) to the list
// items — React reconciliation would strip those on every re-render.
const DesktopCarousel = memo(function DesktopCarousel({
	cards,
	numberOfCards,
	onOpenModal,
}: {
	cards: DiningCard[];
	numberOfCards: number;
	onOpenModal: (modalId: string) => void;
}) {
	return (
		<>
			<div className="AnimatedCardCarousel-container--desktop">
				<ul className="AnimatedCardCarousel--list">
					{cards.map(card => (
						<li key={card.id} className="AnimatedCardCarousel--slide">
							{card.indicator && (
								<div className="IndicatorBackground">
									<div className={`IndicatorColor IndicatorColor--${card.indicator}`} />
									<div className="IndicatorMessage ty-c3">{card.indicatorStatus}</div>
								</div>
							)}
							{/* Flickity adds AnimatedCardCarousel-Card--animate and --active during init */}
							<div className="AnimatedCardCarousel-Card AnimatedCardCarousel-Card--light">
								{card.backgroundImage && (
									<BackgroundImageCard backgroundImage={card.backgroundImage} />
								)}
								<CardBody card={card} onOpenModal={onOpenModal} />
							</div>
						</li>
					))}
				</ul>
			</div>

			{/* Flickity binds click handlers to these buttons by class selector — no onClick needed */}
			<div className="AnimatedCardCarousel-navigation AnimatedCardCarousel-navigation--desktop">
				<button className="AnimatedCardCarousel-pagination--button AnimatedCardCarousel-pagination--prev Button--prevIcon--light">
					<span className="Button-label">Previous slide</span>
				</button>
				<div className="AnimatedCardCarousel-pagination ty-c1">
					{/* Flickity sets text content after init; render empty to avoid stale text */}
					<span
						className="AnimatedCardCarousel-pagination--currentSlideNumber"
						data-number-of-cards={numberOfCards}
					/>
				</div>
				<button className="AnimatedCardCarousel-pagination--button AnimatedCardCarousel-pagination--next Button--nextIcon--light">
					<span className="Button-label">Next slide</span>
				</button>
			</div>
		</>
	);
});

export const PORTAL_WRAPPER = {
	tagName: 'div',
	className: 'AnimatedCardCarousel SpacedComponent container',
	id: 'AnimatedCardCarousel-experiment',
} as const;

export function HotelDiningCarousel() {
	const { enabled } = useHotelDiningCarousel();
	const [carouselData, setCarouselData] = useState<CarouselData | null>(null);
	const [cards, setCards] = useState<DiningCard[] | null>(null);
	const [activeModal, setActiveModal] = useState<string | null>(null);
	const hasFetchedRef = useRef(false);
	const flickityInitRef = useRef(false);

	useEffect(() => {
		if (hasFetchedRef.current) return;
		if (!enabled) return;

		const ctx = getPropertyContext();
		if (!ctx) return;

		hasFetchedRef.current = true;
		const controller = new AbortController();

		const { lang, propertyShortName } = ctx;
		const isStaging = String(getConfig()['ENVIRONMENT_NAME'] ?? '').includes('stage');
		const url = `/content/fshr/pages/${lang}/properties/${propertyShortName}/dining.bl.json${isStaging ? `?cb=${Date.now()}` : ''}`;

		fetch(url, { signal: controller.signal })
			.then(r => (r.ok ? r.json() : null))
			.then((data: any) => {
				if (!data) return;
				const body = data?.general?.body;
				const items = (body?.redesignBodySection ?? body?.body_section)?.items;
				if (!items) {
					console.warn('[HotelDiningCarousel] redesignBodySection.items not found in', url);
					return;
				}
				const carousel: CarouselData | undefined =
					items?.carousel ??
					Object.values(items as Record<string, unknown>).find(
						(item: any) =>
							item?.id?.includes('AnimatedCardCarousel') ||
							item?.heading?.title?.toLowerCase() === 'all food and drink'
					);
				if (!carousel) {
					console.warn('[HotelDiningCarousel] carousel not found in items:', Object.keys(items));
					return;
				}
				const hasGenericCards = Object.values(carousel.cards ?? {}).some(
					(v: any) => v?.genericCard != null
				);
				if (!hasGenericCards) {
					console.warn('[HotelDiningCarousel] unsupported card schema, skipping');
					return;
				}
				const extracted = extractCards(carousel.cards ?? {}).filter(card => card.title);
				if (!extracted.some(card => card.backgroundImage !== null)) return;
				setCarouselData(carousel);
				setCards(extracted);
			})
			.catch(err => {
				if (err.name !== 'AbortError') {
					console.error('[HotelDiningCarousel] fetch error:', err);
				}
			});

		return () => {
			controller.abort();
			hasFetchedRef.current = false;
		};
	}, [enabled]);

	// After cards render, hand the carousel element to fs-blacklight's Flickity init.
	// window.initSingleAnimatedCardCarousel is set at module-eval time in animatedCardCarousel.js,
	// which loads asynchronously — retry until available.
	useEffect(() => {
		if (!cards?.length || flickityInitRef.current) return;
		const el = document.getElementById(PORTAL_WRAPPER.id);
		if (!el) return;

		let cancelled = false;
		let attempts = 0;
		const maxAttempts = 40; // 4 seconds total

		const tryInit = () => {
			if (cancelled || flickityInitRef.current) return;
			const initFn = (window as any).initSingleAnimatedCardCarousel;
			if (typeof initFn === 'function') {
				flickityInitRef.current = true;
				initFn(el);
				return;
			}
			if (++attempts < maxAttempts) {
				setTimeout(tryInit, 100);
			} else {
				console.warn('[HotelDiningCarousel] window.initSingleAnimatedCardCarousel not available');
			}
		};

		tryInit();
		return () => {
			cancelled = true;
		};
	}, [cards]);

	// Stable reference so DesktopCarousel memo comparison doesn't break on re-renders.
	const handleOpenModal = useCallback((id: string) => setActiveModal(id), []);

	if (!cards?.length || !carouselData) return null;

	const heading = carouselData.heading?.title ?? '';

	return (
		<>
			{heading && (
				<div className="Heading AnimatedCardCarousel-heading">
					<h2 className="Heading-title ty-h2">{heading}</h2>
				</div>
			)}

			<DesktopCarousel cards={cards} numberOfCards={cards.length} onOpenModal={handleOpenModal} />

			{/* Mobile stacked layout — Flickity mobile init targets --mobile, not --stacked */}
			<div className="AnimatedCardCarousel-mobile">
				<div className="AnimatedCardCarousel-container--stacked">
					<div className="row">
						{cards.map(card => (
							<div key={card.id} className="AnimatedCardCarousel--slide--stacked col-sm-12">
								{card.indicator && (
									<div className="IndicatorBackground">
										<div className={`IndicatorColor IndicatorColor--${card.indicator}`} />
										<div className="IndicatorMessage ty-c3">{card.indicatorStatus}</div>
									</div>
								)}
								<div className="AnimatedCardCarousel-Card AnimatedCardCarousel-Card--light AnimatedCardCarousel-Card--active">
									{card.backgroundImage && (
										<BackgroundImageCard backgroundImage={card.backgroundImage} />
									)}
									<CardBody card={card} onOpenModal={handleOpenModal} />
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Warning modals rendered outside carousel list to avoid z-index issues */}
			{cards.map(card =>
				card.warningModal ? (
					<WarningModalComponent
						key={card.warningModal.id}
						warningModal={card.warningModal}
						isOpen={activeModal === card.warningModal.id}
						onClose={() => setActiveModal(null)}
					/>
				) : null
			)}
		</>
	);
}
